import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell, Search, Settings, LogOut, HelpCircle, Keyboard,
  Smartphone, ZoomIn, ZoomOut, RotateCcw, ChevronDown,
  Check, X, Building2, Home, MapPin, MessageSquareText,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import type { View } from '../../App';
import { ThemeToggle } from '../ui/ThemeToggle.js';
import { LanguageSwitcher } from '../ui/LanguageSwitcher.js';
import { GlobalSearch } from '../search/GlobalSearch.js';
import { CommandPalette } from '../search/CommandPalette';
import { displayKey } from '../../lib/shortcuts';
import { NotificationPanel, NotificationBadge } from './NotificationPanel.js';
import { StatusPicker, type UserStatus } from '../ui/StatusPicker.js';
import { Popover } from '../ui/Popover.js';
import { useAuthStore, useNotificationStore, useChatStore } from '../../stores';
import { usePresenceStore } from '../../stores/presenceStore';
import type { PresenceStatus, WorkLocation } from '../../stores/presenceStore';
import { api } from '../../lib/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { adminUrl } from '../../lib/urls';

/** Page heading per view; compliance has no nav key of its own */
const viewTitleKey = (v: View) => (v === 'compliance' ? 'topbar.compliance' : `nav.${v}`);

const statusConfig: Record<PresenceStatus, { dot: string }> = {
  available: { dot: 'bg-aurora-3' },
  busy:      { dot: 'bg-nord-11' },
  away:      { dot: 'bg-aurora-2' },
  dnd:       { dot: 'bg-nord-12' },
  offline:   { dot: 'bg-nord-3' },
};

const workLocations: { key: WorkLocation; Icon: typeof Home }[] = [
  { key: 'home', Icon: Home },
  { key: 'office', Icon: Building2 },
  { key: 'offsite', Icon: MapPin },
];


interface TopBarProps {
  currentView: View;
  onNavigate: (view: View) => void;
  onOpenDm?: (userId: string, displayName: string) => void;
  onMobileMenuToggle?: () => void;
  /** Opens the shared keyboard-shortcuts overlay owned by App */
  onShowShortcuts?: () => void;
}

export function TopBar({ currentView, onNavigate: _onNavigate, onOpenDm, onShowShortcuts }: TopBarProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { setActiveRoom } = useChatStore();
  const [searchOpen,    setSearchOpen]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [paletteOpen,   setPaletteOpen]   = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [notifOpen,     setNotifOpen]     = useState(false);
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const [userStatus,    setUserStatus]    = useState<UserStatus | null>(null);
  const { unreadCount } = useNotificationStore();
  const { myStatus: status, myWorkLocation: workLocation, sendPresenceUpdate, setMyWorkLocation } = usePresenceStore();
  const [zoom,          setZoom]          = useState(1);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const notifButtonRef = useRef<HTMLButtonElement>(null);
  const mobileTrapRef = useFocusTrap(mobileOpen);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  // This deployment's own host (customer installs are not app.eurion.se); Tauri has a custom origin
  const appOrigin = window.location.origin.startsWith('http') ? window.location.origin : 'https://app.eurion.se';
  const mobileUrl = `${appOrigin}/mobile`;
  useEffect(() => {
    if (!mobileOpen || qrDataUrl) return;
    void import('qrcode').then(q => q.toDataURL(mobileUrl, { width: 160, margin: 1, color: { dark: '#2E3440', light: '#FFFFFF' } }))
      .then(setQrDataUrl).catch(() => {});
  }, [mobileOpen, qrDataUrl, mobileUrl]);
  const token   = localStorage.getItem('eurion_token') ?? '';

  // Load user status from API on mount
  useEffect(() => {
    if (!user?.id) return;
    api.get<{ user: { statusText: string | null; statusEmoji: string | null; statusExpiresAt: string | null } }>('/v1/users/me')
      .then((res) => {
        if (res.success && res.data?.user) {
          const { statusText, statusEmoji, statusExpiresAt } = res.data.user;
          if (statusText || statusEmoji) {
            setUserStatus({ text: statusText ?? '', emoji: statusEmoji ?? '', expiresAt: statusExpiresAt });
          }
        }
      })
      .catch(() => {});
  }, [user?.id]);

  // Apply zoom to document root
  useEffect(() => {
    document.documentElement.style.fontSize = `${zoom * 100}%`;
    return () => { document.documentElement.style.fontSize = ''; };
  }, [zoom]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Ctrl/⌘+K is handled by App via lib/shortcuts and arrives as 'eurion:palette'
    if (e.key === 'Escape') { setSearchOpen(false); setMenuOpen(false); setMobileOpen(false); setNotifOpen(false); }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const toggle = () => setPaletteOpen(o => !o);
    window.addEventListener('eurion:palette', toggle);
    return () => window.removeEventListener('eurion:palette', toggle);
  }, []);

  const handleLogout = () => {
    const refresh = localStorage.getItem('eurion_refresh_token') ?? '';
    api.post('/v1/auth/logout', { refreshToken: refresh }).catch(() => {});
    api.setToken(null);
    logout();
  };

  /** Deep-link: notification click ? navigate to chat room + scroll to message */
  const handleNotifNavigate = useCallback((type: string, targetId?: string, messageId?: string) => {
    setNotifOpen(false);
    if (!targetId) return;
    if (type === 'message' || type === 'mention' || type === 'reaction') {
      _onNavigate('chat');
      setActiveRoom(targetId);
      // After React renders the room, scroll to the specific message
      if (messageId) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const el = document.getElementById(`msg-${messageId}`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.classList.add('ring-2', 'ring-frost-2', 'ring-opacity-60');
              setTimeout(() => el.classList.remove('ring-2', 'ring-frost-2', 'ring-opacity-60'), 2500);
            }
          }, 300);
        });
      }
    } else if (type === 'file') {
      _onNavigate('files');
    } else if (type === 'call') {
      _onNavigate('meet');
    }
  }, [_onNavigate, setActiveRoom]);

  const initials = user?.displayName
    ? user.displayName.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('')
    : '?';

  const statusDot = statusConfig[status].dot;

  return (
    <>
      {searchOpen && token && (
        <GlobalSearch
          token={token}
          onClose={() => setSearchOpen(false)}
          onOpenDm={onOpenDm}
          initialQuery={searchQuery}
          onOpenMessage={(roomId, messageId) => handleNotifNavigate('message', roomId, messageId)}
        />
      )}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={_onNavigate}
        onSearch={(q) => { setSearchQuery(q); setSearchOpen(true); }}
        onShowShortcuts={() => onShowShortcuts?.()}
      />

      {/* Mobile app / QR modal */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setMobileOpen(false)}>
          <div ref={mobileTrapRef} role="dialog" aria-modal="true" aria-label={t('topbar.mobileAppTitle')} tabIndex={-1} className="w-80 rounded-xl border border-nord-3 bg-nord-1 p-6 shadow-2xl text-center outline-none" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-semibold text-nord-6">{t('topbar.mobileAppTitle')}</h2>
              <button onClick={() => setMobileOpen(false)} aria-label={t('common.close')} className="rounded p-1 text-nord-4 hover:text-nord-6"><X className="h-4 w-4" /></button>
            </div>
            {/* QR code for mobile app download */}
            <div className="mx-auto mb-4 flex h-44 w-44 items-center justify-center rounded-lg bg-white p-2">
              <img
                src={qrDataUrl ?? undefined}
                alt={t('topbar.qrAlt')}
                className="h-40 w-40"
                loading="lazy"
              />
            </div>
            <p className="text-sm font-medium text-nord-6">{t('layoutUi.mobileAppPlatforms', 'EURION for iOS & Android')}</p>
            <p className="mt-1 text-xs text-nord-muted">{mobileUrl.replace(/^https?:\/\//, '')}</p>
            <div className="mt-4 flex gap-2">
              <a href={`${mobileUrl}/ios`} target="_blank" rel="noopener noreferrer"
                className="flex-1 rounded-lg border border-nord-3 py-2 text-xs text-nord-4 hover:bg-nord-2 transition-colors">
                {'App Store'}
              </a>
              <a href={`${mobileUrl}/android`} target="_blank" rel="noopener noreferrer"
                className="flex-1 rounded-lg border border-nord-3 py-2 text-xs text-nord-4 hover:bg-nord-2 transition-colors">
                {'Google Play'}
              </a>
            </div>
          </div>
        </div>
      )}

      <header className="mobile-topbar flex h-14 shrink-0 items-center justify-between border-b border-nord-3/40 bg-nord-1 px-3 md:px-6">
        <div className="flex min-w-0 items-center gap-2">
          {/* Phones navigate with the bottom tab bar, so there is no drawer button here */}
          <h1 className="text-base md:text-lg font-semibold text-nord-6 tracking-tight">{t(viewTitleKey(currentView))}</h1>
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Search — compact on mobile */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label={t('palette.button')}
            aria-keyshortcuts="Control+K Meta+K"
            className="flex items-center gap-2 rounded-lg border border-nord-3/50 bg-nord-2/50 px-2 md:px-3 py-1.5 text-sm max-md:h-11 max-md:w-11 max-md:justify-center max-md:border-0 max-md:bg-transparent text-nord-4 hover:border-nord-3 hover:bg-nord-2 transition-all"
          >
            <Search className="h-3.5 w-3.5 max-md:h-5 max-md:w-5" aria-hidden="true" />
            <span className="hidden sm:inline w-40 truncate text-left text-nord-muted">{t('palette.button')}</span>
            <kbd className="hidden md:inline rounded bg-nord-1 px-1.5 py-0.5 font-mono text-2xs text-nord-muted">{displayKey('Mod')} K</kbd>
          </button>

          <span className="hidden md:inline-flex"><LanguageSwitcher /></span>
          <span className="hidden sm:inline-flex"><ThemeToggle /></span>

          {/* Notifications */}
          <div className="relative">
            <button
              ref={notifButtonRef}
              type="button"
              onClick={() => setNotifOpen(v => !v)}
              className={clsx('relative rounded-nordic p-2 max-md:p-3 hover:bg-nord-2 transition-colors', notifOpen && 'bg-nord-2')}
              aria-label={t('topbar.notifications')}
              aria-haspopup="true"
              aria-expanded={notifOpen}
            >
              <Bell className="h-5 w-5 text-nord-4" />
              <NotificationBadge count={unreadCount} />
            </button>
            <NotificationPanel
              open={notifOpen}
              anchorRef={notifButtonRef}
              onClose={() => setNotifOpen(false)}
              onNavigate={handleNotifNavigate}
              onOpenToday={() => _onNavigate('today')}
            />
          </div>

          {/* User avatar + dropdown */}
          <div className="relative">
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              className="flex items-center gap-1 rounded-lg p-1 max-md:min-h-[44px] hover:bg-nord-2 transition-colors"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t('topbar.userMenu')}
            >
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-nord-10 text-sm font-bold text-white select-none">
                {initials}
                <span className={clsx('absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-nord-1', statusDot)} />
              </div>
              <ChevronDown className={clsx('h-3.5 w-3.5 text-nord-4 transition-transform duration-200', menuOpen && 'rotate-180')} />
            </button>

            <Popover open={menuOpen} anchorRef={menuButtonRef} onClose={() => setMenuOpen(false)} role="menu" aria-label={t('topbar.userMenu')}
              className="w-72 overflow-hidden rounded-xl border border-nord-3/40 bg-nord-1 shadow-menu animate-dropdown-in">

                {/* -- Profile header */}
                <div className="flex items-center gap-3 border-b border-nord-3 px-4 py-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-nord-10 text-sm font-bold text-white">
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-nord-6">{user?.displayName ?? t('layoutUi.userFallback', 'User')}</p>
                    <p className="truncate text-xs text-nord-muted">{user?.email ?? ''}</p>
                  </div>
                  <span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full', statusDot)} title={t(`topbar.status.${status}`)} />
                </div>

                {/* -- Custom status message */}
                <div className="border-b border-nord-3 px-4 py-2">
                  <button
                    onClick={() => { setStatusPickerOpen(true); setMenuOpen(false); }}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-sm text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors"
                  >
                    {userStatus ? (
                      <>
                        <span className="text-base">{userStatus.emoji || '??'}</span>
                        <span className="flex-1 text-left truncate">{userStatus.text || t('topbar.customStatus')}</span>
                      </>
                    ) : (
                      <>
                        <MessageSquareText className="h-4 w-4 shrink-0" />
                        <span className="flex-1 text-left">{t('topbar.setStatus')}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* -- Status */}
                <div className="border-b border-nord-3 py-1">
                  <p className="px-4 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-widest text-nord-muted">{t('topbar.statusHeading')}</p>
                  {(Object.entries(statusConfig) as [PresenceStatus, { dot: string }][]).map(([key, cfg]) => (
                    <button key={key} onClick={() => sendPresenceUpdate(key, workLocation)}
                      className="flex w-full items-center gap-3 px-4 py-1.5 text-sm text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                      <span className={clsx('h-2 w-2 shrink-0 rounded-full', cfg.dot)} />
                      <span className="flex-1 text-left">{t(`topbar.status.${key}`)}</span>
                      {status === key && <Check className="h-3.5 w-3.5 text-aurora-3" />}
                    </button>
                  ))}
                </div>

                {/* -- Work location */}
                <div className="border-b border-nord-3 py-1">
                  <p className="px-4 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-widest text-nord-muted">{t('topbar.locationHeading')}</p>
                  {workLocations.map(({ key, Icon }) => (
                    <button key={key} onClick={() => { setMyWorkLocation(key); sendPresenceUpdate(status, key); }}
                      className="flex w-full items-center gap-3 px-4 py-1.5 text-sm text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 text-left">{t(`topbar.location.${key}`)}</span>
                      {workLocation === key && <Check className="h-3.5 w-3.5 text-aurora-3" />}
                    </button>
                  ))}
                </div>

                {/* -- Profile / Settings */}
                <div className="border-b border-nord-3 py-1">
                  <a href={adminUrl()} target="_blank" rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                    className="flex w-full items-center gap-3 px-4 py-1.5 text-sm text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                    <Settings className="h-4 w-4 shrink-0" aria-hidden="true" /> {t('topbar.adminPortal')}
                  </a>
                </div>

                {/* -- Zoom */}
                <div className="border-b border-nord-3 px-4 py-2.5">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-nord-muted">{t('topbar.zoom')}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setZoom(z => Math.max(0.7, parseFloat((z - 0.1).toFixed(1))))} aria-label={t('topbar.zoomOut')}
                      className="rounded-lg border border-nord-3 p-1.5 text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                      <ZoomOut className="h-3.5 w-3.5" />
                    </button>
                    <span className="flex-1 text-center text-sm font-medium text-nord-6">{Math.round(zoom * 100)}%</span>
                    <button onClick={() => setZoom(z => Math.min(1.5, parseFloat((z + 0.1).toFixed(1))))} aria-label={t('topbar.zoomIn')}
                      className="rounded-lg border border-nord-3 p-1.5 text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                      <ZoomIn className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => setZoom(1)} aria-label={t('topbar.zoomReset')}
                      className="rounded-lg border border-nord-3 p-1.5 text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* -- Help */}
                <div className="border-b border-nord-3 py-1">
                  <button onClick={() => { onShowShortcuts?.(); setMenuOpen(false); }}
                    className="flex w-full items-center gap-3 px-4 py-1.5 text-sm text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                    <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" /> {t('topbar.help')}
                  </button>
                  <button onClick={() => { onShowShortcuts?.(); setMenuOpen(false); }}
                    className="flex w-full items-center gap-3 px-4 py-1.5 text-sm text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                    <Keyboard className="h-4 w-4 shrink-0" aria-hidden="true" /> {t('shortcuts.title')}
                  </button>
                  <button onClick={() => { setMobileOpen(true); setMenuOpen(false); }}
                    className="flex w-full items-center gap-3 px-4 py-1.5 text-sm text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors">
                    <Smartphone className="h-4 w-4 shrink-0" aria-hidden="true" /> {t('topbar.mobileApp')}
                  </button>
                </div>

                {/* -- Sign out */}
                <div className="py-1">
                  <button onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-1.5 text-sm text-nord-11 hover:bg-nord-11/10 transition-colors">
                    <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" /> {t('common.logout')}
                  </button>
                </div>

            </Popover>
          </div>
        </div>
      </header>

      {statusPickerOpen && (
        <StatusPicker
          currentStatus={userStatus}
          onStatusSet={(s) => setUserStatus(s)}
          onClose={() => setStatusPickerOpen(false)}
        />
      )}
    </>
  );
}
